import {
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysSite } from '../queryKeys';

import { fetchSitesAdmin } from './fetchSitesAdmin';
import { type SitesAdminQueryParams, type SitesAdminResponse } from './types';

/**
 * Generates the query options for one page of the site directory.
 *
 * @param queryParameters - Search string, scope, id and page of the listing.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getSitesAdminQueryOptions = <TData = SitesAdminResponse>(
  queryParameters: SitesAdminQueryParams,
  options?: Omit<
    UseQueryOptions<SitesAdminResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchSitesAdmin(queryParameters, { signal }),
    queryKey: queryKeysSite.getAllAdmin(queryParameters),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query hook for one page of the site directory (vadmin only).
 *
 * @param queryParameters - Search string, scope, id and page of the listing.
 * @param options - Additional React Query options.
 * @returns Query result containing one page of sites.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Directory/operation/get_sites_directory_vadmin_site_get
 */
// The options factory covers callers that hold a query client directly, so this
// hook can stand without one; it is the endpoint's entry point for components.
// fallow-ignore-next-line unused-export
export const useQuerySitesAdmin = <TData = SitesAdminResponse>(
  queryParameters: SitesAdminQueryParams,
  options?: Omit<
    UseQueryOptions<SitesAdminResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getSitesAdminQueryOptions<TData>(queryParameters, options));
